// 2026-10-04 addition — real counterpart to labariumaiportal's own /admin
// "User Dashboard" page (AdminDashboard.js there), confirmed as a genuine
// gap, distinct from this app's own RCM panels: that page is a unified
// roster view — stat tiles (Total/Patients/Clinicians/Paid/Pending), ONE
// combined name/email/phone search across BOTH roles in a single table —
// where RcmPatientPanel/RcmClinicianPanel are split by role and scoped to
// payment workflows (resend-link, receipts). This mirrors portal's own
// layout/columns/filters as closely as the data shape allows, reusing the
// already-fixed /api/admin/rcm/patients and /api/admin/rcm/clinicians
// endpoints (no new backend endpoint needed — same two lists RCM already
// reads, just merged and summarized client-side the same way portal's own
// page does it).
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { extractErrorDetail } from "./validators";
import {
  Box, Container, Typography, TextField, MenuItem, Select, InputLabel, FormControl, FormHelperText,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, CircularProgress, Alert, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import LockReset from "@mui/icons-material/LockReset";
import AlternateEmail from "@mui/icons-material/AlternateEmail";
import axios from "axios";
import AdminPageShell from "./AdminPageShell";
import { formatEST } from "../dateUtils";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000";
const BRAND = { navy: "#00357a", gold: "#c9a96e" };

// 2026-10-23 addition (sales-rep lab-brand routing) — admin isn't tied
// to one brand, so this invite surface requires an explicit pick too,
// no default. Hand-duplicated across both apps' frontends wherever this
// same picker is needed (same convention as AdminSalesReps.js/
// AdminClinicians.js's own identical constants on the portal side) —
// keep exactly in sync with labariumaiportal's main.py
// LAB_BRAND_DISPLAY_NAMES.
const LAB_BRAND_OPTIONS = [
  { value: "infiniti", label: "InfinitiLabs" },
  { value: "apex", label: "ApexLabs" },
];

const PAYMENT_STATUS_COLOR = { completed: "success", pending: "warning", failed: "error" };

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("admin_token")}` };
}

function StatTile({ label, value, isDark }) {
  return (
    <Box sx={{
      flex: 1, minWidth: 140, p: 2, borderRadius: 2,
      bgcolor: isDark ? "#111827" : "#fff",
      border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,53,122,0.1)",
    }}>
      <Typography sx={{ fontSize: 13.5, fontWeight: 700, letterSpacing: "0.05em",
        color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase", mb: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 26, fontWeight: 800, color: isDark ? "#c9a96e" : BRAND.navy }}>
        {value}
      </Typography>
    </Box>
  );
}

// 2026-10-14 fix (admin parity gap, confirmed real, reported directly) —
// this page is the real counterpart to labariumaiportal's own
// AdminDashboard.js (see this file's own header comment), but had ONLY the
// audit-log icon — no account-recovery actions at all, while the portal
// side has both Reset Credentials and Change Email. Proxies into this
// app's own new /api/admin/portal-users/reset-credentials and
// .../change-email routes (which themselves proxy into labariumaiportal's
// /internal/admin-*-user-* routes) — recreated here rather than
// cross-imported from labariumaiportal/frontend's AdminUserRecoveryActions.js
// since these are separate frontend packages (same convention
// SHARED_SECTION_IDS in LabOrdersView.js already documents for this app).
// 2026-10-22 (Piece 2, one-identity-one-email invariant) — exported so
// IdentityLinking.js can render the SAME change-email/reset-credentials
// actions inline on a search result that has a linked patient account,
// rather than re-implementing this dialog a second time (advisor-flagged:
// avoids a brand-new ?sub= URL-param deep-link into this page, which this
// page has never supported).
export function PortalUserRecoveryActions({ sub, email, onChanged }) {
  const [dialog, setDialog] = useState(null); // "reset" | "change-email" | null
  const [newEmail, setNewEmail] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null); // {email_sent, temp_password?}

  // 2026-10-22 (Piece 2, ui-ux-checker finding) — IdentityLinking.js's
  // search results table otherwise goes stale after an inline email
  // change here: the row keeps showing the OLD claimed_email/
  // linked_account_email until the admin manually re-searches. Fired
  // only once the admin has dismissed the success dialog themselves
  // (deferred-until-close, same convention AdminUserRecoveryActions.js's
  // sibling component already uses), and only for change-email (a reset
  // doesn't change anything this table displays).
  const closeDialog = () => {
    if (submitting) return;
    const wasSuccessfulChangeEmail = dialog === "change-email" && result;
    setDialog(null); setNewEmail(""); setReason(""); setError(null); setResult(null);
    if (wasSuccessfulChangeEmail) onChanged && onChanged();
  };

  const doReset = async () => {
    setSubmitting(true); setError(null);
    try {
      const res = await axios.post(`${API}/api/admin/portal-users/reset-credentials`, { sub }, { headers: authHeaders() });
      setResult(res.data);
    } catch (err) {
      setError(extractErrorDetail(err, "Could not reset credentials for this user."));
    } finally {
      setSubmitting(false);
    }
  };

  const doChangeEmail = async () => {
    setSubmitting(true); setError(null);
    try {
      const res = await axios.post(
        `${API}/api/admin/portal-users/change-email`,
        { sub, new_email: newEmail.trim(), reason: reason.trim() },
        { headers: authHeaders() },
      );
      setResult(res.data);
    } catch (err) {
      setError(extractErrorDetail(err, "Could not change this user's email."));
    } finally {
      setSubmitting(false);
    }
  };

  if (!sub) return null;

  return (
    <>
      <Tooltip title="Reset credentials">
        <IconButton size="small" onClick={() => setDialog("reset")}>
          <LockReset fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Change email">
        <IconButton size="small" onClick={() => setDialog("change-email")}>
          <AlternateEmail fontSize="small" />
        </IconButton>
      </Tooltip>

      <Dialog open={dialog === "reset"} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, color: BRAND.navy }}>Reset Credentials</DialogTitle>
        <DialogContent>
          {result ? (
            result.email_sent ? (
              <Alert severity="success" sx={{ mt: 1 }}>
                New temporary credentials were generated and emailed to {email || "this user's address on file"}.
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ mt: 1 }}>
                New temporary credentials were generated, but the email could not be sent. The password HAS already
                been reset — deliver this temporary password yourself:
                {" "}<strong style={{ fontFamily: "monospace" }}>{result.temp_password}</strong>
              </Alert>
            )
          ) : (
            <>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              This will generate new temporary credentials for {email ? <strong>{email}</strong> : "this user"} — continue?
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {result ? (
            <Button onClick={closeDialog} variant="contained" sx={{ bgcolor: BRAND.navy }}>Done</Button>
          ) : (
            <>
              <Button onClick={closeDialog} disabled={submitting}>Cancel</Button>
              <Button onClick={doReset} variant="contained" disabled={submitting} sx={{ bgcolor: BRAND.navy }}>
                {submitting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Reset Credentials"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={dialog === "change-email"} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, color: BRAND.navy }}>Change Email</DialogTitle>
        <DialogContent>
          {result ? (
            <>
              {result.email_sent ? (
                <Alert severity="success" sx={{ mt: 1 }}>
                  This account's login email was changed to <strong>{newEmail.trim()}</strong>. New temporary credentials
                  were emailed to the new address — the previous address can no longer sign in.
                </Alert>
              ) : (
                <Alert severity="error" sx={{ mt: 1 }}>
                  This account's login email was changed to <strong>{newEmail.trim()}</strong>, but the credentials
                  email FAILED to send. The user is locked out until you deliver this temporary password yourself:
                  {" "}<strong style={{ fontFamily: "monospace" }}>{result.temp_password}</strong>
                </Alert>
              )}
              {/* 2026-10-22 (Piece 2, advisor-flagged) — the account's own
                  email change always auto-syncs this patient's identity
                  claim (see _admin_change_user_email_core's docstring); if
                  that secondary write fails, it must never be silent —
                  this is the one place a human sees this response. */}
              {result.identity_claim_synced === false && (
                <Alert severity="warning" sx={{ mt: 1.5 }}>
                  The login email changed successfully, but this patient's identity record could not be
                  automatically updated to match. Use the Identity Linking dashboard to correct it by hand.
                </Alert>
              )}
            </>
          ) : (
            <>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              <Alert severity="info" sx={{ mb: 2 }}>
                This also resets the account's password — new temporary credentials will be emailed to the new
                address, and the current address will no longer be able to log in.
              </Alert>
              <TextField
                label="New email" type="email" fullWidth sx={{ mb: 2, mt: 0.5 }}
                value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
                helperText={email ? `Current: ${email}` : " "}
              />
              <TextField
                label="Reason for change" required fullWidth multiline minRows={2}
                value={reason} onChange={(e) => setReason(e.target.value)}
                helperText="Required — recorded in the audit log."
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {result ? (
            <Button onClick={closeDialog} variant="contained" sx={{ bgcolor: BRAND.navy }}>Done</Button>
          ) : (
            <>
              <Button onClick={closeDialog} disabled={submitting}>Cancel</Button>
              <Button onClick={doChangeEmail} variant="contained"
                disabled={submitting || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(newEmail.trim()) || !reason.trim()}
                sx={{ bgcolor: BRAND.navy }}>
                {submitting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Change Email"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

// 2026-10-23 fix (clinician invite-only build, Piece 3) — public clinician
// self-registration is closed, and admin-created-with-upfront-practice/NPI
// is replaced by this same email-only invite everywhere (decision 8: one
// consistent path, not two). Proxies into this app's own
// /api/admin/portal-users/clinicians/invite route (which itself proxies
// into labariumaiportal's own /internal/admin-invite-clinician) — same
// cross-service pattern already proven by PortalUserRecoveryActions above.
// The invited clinician fills in their own name/practice/NPI/signature on
// first login (the identity-completion gate, a later piece) — this dialog
// no longer collects any of that upfront.
function NewPortalClinicianDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [labBrand, setLabBrand] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleClose = () => {
    if (submitting) return;
    const wasSuccess = !!success;
    setOpen(false);
    setEmail("");
    setLabBrand("");
    setError(null); setSuccess(null);
    if (wasSuccess) onCreated && onCreated();
  };

  const canSubmit = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim()) && !!labBrand;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true); setError(null);
    try {
      const res = await axios.post(`${API}/api/admin/portal-users/clinicians/invite`, {
        email: email.trim(), lab_brand: labBrand,
      }, { headers: authHeaders() });
      // 2026-10-23 fix (security-auditor finding) — this used to show the
      // same "Invite sent" success alert regardless of whether the email
      // actually went out, silently telling the admin it worked even on
      // email_sent: false (and never surfacing the returned temp_password,
      // the only way to hand it to the clinician another way).
      setSuccess({
        email: res.data?.email || email.trim(),
        emailSent: res.data?.email_sent !== false,
        tempPassword: res.data?.temp_password || null,
      });
    } catch (err) {
      setError(extractErrorDetail(err, "Could not send this invite."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button variant="contained" onClick={() => setOpen(true)} sx={{ bgcolor: BRAND.navy, textTransform: "none", fontWeight: 700 }}>
        + Invite Clinician
      </Button>
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, color: BRAND.navy }}>Invite Clinician</DialogTitle>
        <DialogContent>
          {success ? (
            success.emailSent ? (
              <Alert severity="success" sx={{ mt: 1 }}>
                Invite sent to <strong>{success.email}</strong>. They'll set their own password and complete
                their profile (name, practice, NPI, signature) on first login.
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Account created for <strong>{success.email}</strong>, but the invite email could not be
                sent. Hand this temporary password to the clinician directly:
                <Box component="code" sx={{ display: "block", mt: 1, fontWeight: 700 }}>{success.tempPassword}</Box>
              </Alert>
            )
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
              {error && <Alert severity="error">{error}</Alert>}
              <TextField label="Email" type="email" value={email} fullWidth autoFocus
                helperText="They'll receive a temporary password and finish setting up their own account."
                onChange={(e) => setEmail(e.target.value)} />
              <FormControl fullWidth>
                <InputLabel id="portal-clinician-invite-lab-brand-label">Lab Brand</InputLabel>
                <Select
                  labelId="portal-clinician-invite-lab-brand-label" label="Lab Brand" value={labBrand}
                  onChange={(e) => setLabBrand(e.target.value)}
                >
                  {LAB_BRAND_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Determines which lab this clinician's orders and reports are routed to — can't be changed later.
                </FormHelperText>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {success ? (
            <Button onClick={handleClose} variant="contained" sx={{ bgcolor: BRAND.navy }}>Done</Button>
          ) : (
            <>
              <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
              <Button onClick={submit} variant="contained" disabled={submitting || !canSubmit} sx={{ bgcolor: BRAND.navy }}>
                {submitting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Send Invite"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

// 2026-10-26 addition (confirmed real gap, user-directed — "fix
// everything" sweep) — this app's admin panel had no way to create a
// sales rep at all; that only existed inside labariumaiportal's own,
// separate admin login, which an admin working from this panel wouldn't
// know to go find. Mirrors NewPortalClinicianDialog directly above
// (same dialog shape/conventions), plus the name/phone fields a rep
// account needs that a clinician invite doesn't (a rep's identity isn't
// self-claimed later the way a clinician's is — see labariumaiportal's
// own AdminCreateSalesRepRequest docstring).
function NewPortalSalesRepDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [labBrand, setLabBrand] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleClose = () => {
    if (submitting) return;
    const wasSuccess = !!success;
    setOpen(false);
    setName(""); setEmail(""); setPhone(""); setLabBrand("");
    setError(null); setSuccess(null);
    if (wasSuccess) onCreated && onCreated();
  };

  const canSubmit = name.trim() && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim()) && phone.trim() && !!labBrand;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true); setError(null);
    try {
      const res = await axios.post(`${API}/api/admin/portal-users/sales-reps/create`, {
        name: name.trim(), email: email.trim(), phone: phone.trim(), lab_brand: labBrand,
      }, { headers: authHeaders() });
      setSuccess({
        email: res.data?.email || email.trim(),
        emailSent: res.data?.email_sent !== false,
        tempPassword: res.data?.temp_password || null,
      });
    } catch (err) {
      setError(extractErrorDetail(err, "Could not create this account."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button variant="outlined" onClick={() => setOpen(true)} sx={{ borderColor: BRAND.navy, color: BRAND.navy, textTransform: "none", fontWeight: 700 }}>
        + Create Sales Rep
      </Button>
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, color: BRAND.navy }}>Create Sales Representative</DialogTitle>
        <DialogContent>
          {success ? (
            success.emailSent ? (
              <Alert severity="success" sx={{ mt: 1 }}>
                Account created for <strong>{success.email}</strong> and the welcome email was sent.
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Account created for <strong>{success.email}</strong>, but the welcome email could not be
                sent. Hand this temporary password to them directly:
                <Box component="code" sx={{ display: "block", mt: 1, fontWeight: 700 }}>{success.tempPassword}</Box>
              </Alert>
            )
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
              {error && <Alert severity="error">{error}</Alert>}
              <TextField label="Full Name" value={name} fullWidth autoFocus onChange={(e) => setName(e.target.value)} />
              <TextField label="Email" type="email" value={email} fullWidth onChange={(e) => setEmail(e.target.value)} />
              <TextField label="Phone" value={phone} fullWidth onChange={(e) => setPhone(e.target.value)} />
              <FormControl fullWidth>
                <InputLabel id="portal-sales-rep-create-lab-brand-label">Lab Brand</InputLabel>
                <Select
                  labelId="portal-sales-rep-create-lab-brand-label" label="Lab Brand" value={labBrand}
                  onChange={(e) => setLabBrand(e.target.value)}
                >
                  {LAB_BRAND_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Determines which lab's clinicians this rep can invite — can't be changed later.
                </FormHelperText>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {success ? (
            <Button onClick={handleClose} variant="contained" sx={{ bgcolor: BRAND.navy }}>Done</Button>
          ) : (
            <>
              <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
              <Button onClick={submit} variant="contained" disabled={submitting || !canSubmit} sx={{ bgcolor: BRAND.navy }}>
                {submitting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Create Account"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

export default function UserDashboard() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [patients,   setPatients]   = useState([]);
  const [clinicians, setClinicians] = useState([]);
  const [loading,     setLoading]   = useState(true);
  const [error,       setError]     = useState("");

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");

  const fetchAll = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [pRes, cRes] = await Promise.all([
        axios.get(`${API}/api/admin/rcm/patients`, { headers: authHeaders() }),
        axios.get(`${API}/api/admin/rcm/clinicians`, { headers: authHeaders() }),
      ]);
      if (pRes.data.error || cRes.data.error) {
        setError(pRes.data.error || cRes.data.error);
      }
      setPatients(pRes.data.patients || []);
      setClinicians(cRes.data.clinicians || []);
    } catch (err) {
      if (err?.response?.status === 401) { localStorage.removeItem("admin_token"); navigate("/admin-login"); return; }
      setError(extractErrorDetail(err, "Failed to load users."));
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem("admin_token")) { navigate("/admin-login"); return; }
    fetchAll();
  }, [fetchAll, navigate]);

  // Merge both lists into one unified roster, same shape portal's own
  // page uses (role tags which collection each row came from).
  const users = useMemo(() => [
    ...patients.map((p) => ({ ...p, role: "patient" })),
    ...clinicians.map((c) => ({ ...c, role: "clinician" })),
  ], [patients, clinicians]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      if (paymentStatusFilter !== "all" && (u.payment_status || "") !== paymentStatusFilter) return false;
      if (q) {
        const hay = `${u.first_name || ""} ${u.last_name || ""} ${u.email || ""} ${u.phone || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [users, search, roleFilter, paymentStatusFilter]);

  const stats = useMemo(() => ({
    total: users.length,
    patients: patients.length,
    clinicians: clinicians.length,
    paid: patients.filter((p) => p.payment_status === "completed").length,
    pending: patients.filter((p) => p.payment_status === "pending").length,
  }), [users, patients, clinicians]);

  return (
    <AdminPageShell
      isDark={isDark}
      backPath="/admin"
      title="User Dashboard"
      actions={
        <Box sx={{ display: "flex", gap: 1.5 }}>
          {/* 2026-10-14 fix (admin parity gap, confirmed real, reported
             directly) — labariumaiportal's own admin page can create a
             new clinician account for this ecosystem; this page had no
             equivalent at all. */}
          <NewPortalClinicianDialog onCreated={fetchAll} />
          {/* 2026-10-26 addition — same gap, for sales reps (see
             NewPortalSalesRepDialog's own comment above). */}
          <NewPortalSalesRepDialog onCreated={fetchAll} />
        </Box>
      }
    >
      {/* Content width matches AdminDashboard.js's canonical
          Container maxWidth="md" exactly — this page previously used a
          plain Box with maxWidth:1400, the "RCM is smaller, Users is
          wider" gap reported directly. */}
      <Container maxWidth="md" sx={{ pb: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
        ) : (
          <>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
              <StatTile label="Total users" value={stats.total} isDark={isDark} />
              <StatTile label="Patients" value={stats.patients} isDark={isDark} />
              <StatTile label="Clinicians" value={stats.clinicians} isDark={isDark} />
              <StatTile label="Paid" value={stats.paid} isDark={isDark} />
              <StatTile label="Payment pending" value={stats.pending} isDark={isDark} />
            </Box>

            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
              <TextField
                size="small" label="Search name, email, phone"
                value={search} onChange={(e) => setSearch(e.target.value)}
                sx={{ minWidth: 260, bgcolor: isDark ? "#111827" : "#fff" }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Role</InputLabel>
                <Select label="Role" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} sx={{ bgcolor: isDark ? "#111827" : "#fff" }}>
                  <MenuItem value="all">All roles</MenuItem>
                  <MenuItem value="patient">Patient</MenuItem>
                  <MenuItem value="clinician">Clinician</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel>Payment status</InputLabel>
                <Select label="Payment status" value={paymentStatusFilter} onChange={(e) => setPaymentStatusFilter(e.target.value)} sx={{ bgcolor: isDark ? "#111827" : "#fff" }}>
                  <MenuItem value="all">All statuses</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* 2026-10-14 fix — real dark-mode bug, confirmed by UI audit:
                an unstyled MUI `Paper` resolves `background.paper`, which
                MUI's own dark-mode default sets to near-black (#121212) —
                this table used to render as a jarring near-black card
                floating on an otherwise light #F7F7F7 page shell whenever
                dark mode was toggled from elsewhere (theme mode is global,
                not per-page). Explicit isDark-aware bgcolor instead of
                relying on Paper's own default, matching every other
                bordered card on this page. */}
            <TableContainer component={Paper} sx={{
              borderRadius: 2,
              bgcolor: isDark ? "#111827" : "#fff",
              border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,53,122,0.1)",
            }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: isDark ? "#94a3b8" : undefined, borderBottom: isDark ? "1px solid rgba(255,255,255,0.1)" : undefined }}>Name</TableCell>
                    <TableCell sx={{ color: isDark ? "#94a3b8" : undefined, borderBottom: isDark ? "1px solid rgba(255,255,255,0.1)" : undefined }}>Email</TableCell>
                    <TableCell sx={{ color: isDark ? "#94a3b8" : undefined, borderBottom: isDark ? "1px solid rgba(255,255,255,0.1)" : undefined }}>Phone</TableCell>
                    <TableCell sx={{ color: isDark ? "#94a3b8" : undefined, borderBottom: isDark ? "1px solid rgba(255,255,255,0.1)" : undefined }}>Role</TableCell>
                    <TableCell sx={{ color: isDark ? "#94a3b8" : undefined, borderBottom: isDark ? "1px solid rgba(255,255,255,0.1)" : undefined }}>DOB</TableCell>
                    <TableCell sx={{ color: isDark ? "#94a3b8" : undefined, borderBottom: isDark ? "1px solid rgba(255,255,255,0.1)" : undefined }}>Created</TableCell>
                    <TableCell sx={{ color: isDark ? "#94a3b8" : undefined, borderBottom: isDark ? "1px solid rgba(255,255,255,0.1)" : undefined }}>Payment status</TableCell>
                    <TableCell sx={{ color: isDark ? "#94a3b8" : undefined, borderBottom: isDark ? "1px solid rgba(255,255,255,0.1)" : undefined }}>Paid at</TableCell>
                    <TableCell sx={{ color: isDark ? "#94a3b8" : undefined, borderBottom: isDark ? "1px solid rgba(255,255,255,0.1)" : undefined }}>Audit</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: "text.disabled" }}>No users match these filters.</TableCell></TableRow>
                  ) : filtered.map((u) => (
                    <TableRow key={`${u.role}-${u.id}`} hover sx={{ "& .MuiTableCell-root": { color: isDark ? "#e5e7eb" : "inherit", borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : undefined } }}>
                      <TableCell>{u.first_name} {u.last_name}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: 13 }}>{u.email}</TableCell>
                      <TableCell>{u.phone || "—"}</TableCell>
                      <TableCell sx={{ textTransform: "capitalize" }}>{u.role}</TableCell>
                      <TableCell>{u.date_of_birth || "—"}</TableCell>
                      <TableCell>{formatEST(u.created_at, { dateOnly: true })}</TableCell>
                      <TableCell>
                        {u.role === "patient"
                          ? <Chip size="small" label={u.payment_status || "pending"} color={PAYMENT_STATUS_COLOR[u.payment_status] || "default"} />
                          : "—"}
                      </TableCell>
                      <TableCell>{u.role === "patient" ? formatEST(u.payment_confirmed_at, { dateOnly: true }) : "—"}</TableCell>
                      <TableCell>
                        {/* 2026-10-08 addition (audit click-through) — same-app
                            navigate() straight into AuditDashboard pre-filtered
                            to this one person, "portal" source + their email
                            (the identity value record_audit_event actually logs
                            for registration/login/payment on portal's side —
                            see get_portal_audit_log's sub-resolution fallback
                            for clinician-only actions logged under sub instead). */}
                        {u.email && (
                          <Tooltip title="View audit log">
                            <IconButton size="small"
                              onClick={() => navigate(`/admin/audit-log?source=portal&identity=${encodeURIComponent(u.email)}`)}>
                              <HistoryIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <PortalUserRecoveryActions sub={u.sub} email={u.email} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Container>
    </AdminPageShell>
  );
}
